/** A care programme's register on this device: everyone in care, most pressing first. */
import { type FollowupState, type Pack, openEpisodes, quietHours, worklist } from '@assist/core';
import { useBundles } from '../care.js';
import { ecClock } from '../components/SeriesCard.js';
import { clock } from '../controller.js';
import { eth } from '../dates.js';
import { useApp } from '../hooks.js';
import { t } from '../i18n.js';
import { activePacks } from '../packs.js';
import { displayName, mrnOf } from '../patient.js';
import { programmeTitle } from '../shell.js';

const TONE: Record<FollowupState, string> = {
  lost: 'red',
  overdue: 'red',
  due: 'amber',
  promised: 'amber',
  upcoming: 'green',
};

export function Programme({ code }: { code: string }) {
  const a = useApp();
  const bundles = useBundles();
  const found = activePacks(a.packSettings)
    .map((x) => x.pack)
    .flatMap((pk) => pk.programmes.filter((p) => p.code === code).map((p) => ({ pk, p })))[0];
  if (!found)
    return (
      <div class="card">
        <h3>{t.grpProgrammes}</h3>
        <p class="muted">{t.noPacks}</p>
      </div>
    );
  const today = clock.today();
  const programme = found.p;
  const pack: Pack = { ...found.pk, programmes: [programme] };
  if (programme.episode) return <EpisodeRegister pack={pack} />;
  const rows = bundles ? worklist(bundles, [pack], today) : null;
  const progName = (code: string) => pack.programmes.find((p) => p.code === code)?.display ?? code;
  const label: Record<FollowupState, string> = {
    lost: t.tabLost,
    overdue: t.tabOverdue,
    due: t.tabDue,
    promised: t.tabPromised,
    upcoming: t.upcoming,
  };
  return (
    <div class="card" data-programme={pack.programmes[0]?.code}>
      <h3>{programmeTitle(pack.programmes[0] as Pack['programmes'][number])}</h3>
      <p class="muted">{rows ? t.inCare(rows.length) : t.computing}</p>
      {rows && !rows.length ? <p class="empty">{t.noneInCare}</p> : null}
      {rows?.length ? (
        <table class="rtable">
          <thead>
            <tr>
              <th>{t.nameCol}</th>
              <th>{t.grpProgrammes}</th>
              <th>{t.lastVisitCol}</th>
              <th>{t.dueCol}</th>
              <th>{t.statusCol}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ patient, status, programmes }) => (
              <tr key={patient.id}>
                <td class="rt-title" data-label={t.nameCol}>
                  <a href={`#/patient/${patient.id}`}>{displayName(patient)}</a>
                  {mrnOf(patient) ? <div class="muted">MRN {mrnOf(patient)}</div> : null}
                </td>
                <td data-label={t.grpProgrammes}>{programmes.map(progName).join(', ')}</td>
                <td data-label={t.lastVisitCol}>
                  {status.lastVisit ? eth(status.lastVisit) : '—'}
                </td>
                <td data-label={t.dueCol}>{eth(status.dueDate)}</td>
                <td data-label={t.statusCol}>
                  <span class={`pill ${TONE[status.state]}`}>
                    {label[status.state]}
                    {status.daysLate > 0 && status.state !== 'promised'
                      ? ` · ${t.daysLate(status.daysLate)}`
                      : ''}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

/** An episode programme (the labour ward): everyone with the episode open, the latest first. */
function EpisodeRegister({ pack }: { pack: Pack }) {
  const bundles = useBundles();
  const rows = bundles
    ? openEpisodes(bundles, pack.programmes).map((x) => {
        const b = bundles.find((y) => y.patient.id === x.patient.id);
        const last = (b?.record.encounters ?? [])
          .map((e) => e.period.start)
          .filter((d) => d >= (x.episode.period?.start ?? ''))
          .sort()
          .pop();
        // nothing recorded for too long (a woman who left with the episode still open)
        const prog = x.programme;
        const hours = quietHours(x.episode, b?.record.encounters ?? [], clock.now());
        const stale = !!prog?.review_after_hours && hours >= prog.review_after_hours;
        return { ...x, last, stale, hours };
      })
    : null;
  const staleN = rows?.filter((r) => r.stale).length ?? 0;
  return (
    <div class="card" data-programme={pack.programmes[0]?.code}>
      <h3>{programmeTitle(pack.programmes[0] as Pack['programmes'][number])}</h3>
      <p class="muted">
        {rows ? t.inCare(rows.length) : t.computing}
        {staleN ? <span class="pill amber">{t.staleCount(staleN)}</span> : null}
      </p>
      {rows && !rows.length ? <p class="empty">{t.noneInCare}</p> : null}
      {rows?.length ? (
        <table class="rtable">
          <thead>
            <tr>
              <th>{t.nameCol}</th>
              <th>MRN</th>
              <th>{t.sinceCol}</th>
              <th>{t.lastAssessmentCol}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ patient, episode, last, stale, hours }) => (
              <tr key={episode.id} data-stale={stale ? '' : undefined}>
                <td class="rt-title" data-label={t.nameCol}>
                  <a href={`#/patient/${patient.id}`}>{displayName(patient)}</a>
                </td>
                <td data-label="MRN">{mrnOf(patient) ?? '—'}</td>
                <td data-label={t.sinceCol}>
                  {episode.period?.start ? eth(episode.period.start) : '—'}
                </td>
                <td data-label={t.lastAssessmentCol}>
                  {last ? `${eth(last)} · ${ecClock(last)}` : '—'}
                  {stale ? <div class="late small">{t.staleEntry(hours)}</div> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
